import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { isOrgAdminUser } from '../utils/orgRoles';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Issue #663 phase 3 — fail-closed defaults. The two pre-provider defaults the
// audit found fail-OPEN (hasPillarAccess:()=>true, isReadOnly:()=>false) are
// flipped: before the payload is known, the only safe answers are "no pillar
// access" and "read-only".
const PermissionsContext = createContext({
    permissions: [], role: '', can: () => false,
    orgRole: 'member', pillarRoles: {},
    effective: [], scoped: [], contactScope: undefined,
    permissionsLoading: true, permissionsUnknown: true, permissionsError: false,
    refetch: () => {},
    hasPillarAccess: () => false, isReadOnly: () => true,
    canInPillar: () => false, isOrgAdmin: () => false,
    // #860 C6: a tree with no provider (e.g. a component test) fails closed.
    hasEffectivePermission: () => false,
});

export function PermissionsProvider({ children }) {
    const { user, token, getAuthHeader } = useAuth();
    const [permissions, setPermissions] = useState([]);
    const [role, setRole] = useState('');
    const [orgRole, setOrgRole] = useState('member');
    const [pillarRoles, setPillarRoles] = useState({});
    // Issue #663 phase 3 additions — the new payload keys and the sentinel trio.
    const [effective, setEffective] = useState([]);
    const [scoped, setScoped] = useState([]);
    // Undefined is intentional while the permission payload is loading or failed.
    // Consumers must not turn an unknown visibility scope into an "all" view.
    const [contactScope, setContactScope] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const loadPermissions = useCallback(async () => {
        if (!user) {
            if (token || localStorage.getItem('crm_token')) {
                // A token exists but /auth/me has not resolved the user yet
                // (cold load / page refresh). This is an UNKNOWN state, not a
                // known-empty one: parking here keeps permissionsUnknown true
                // so guards that must "never decide while unknown" (the #662
                // settings route, the #663 fail-closed default) wait for the
                // real answer instead of bouncing a legitimately entitled
                // manager off the fail-closed empty store (F-002). The wait is
                // bounded: once /auth/me settles, user becomes non-null and
                // this fetch runs; if the token is rejected, AuthContext
                // clears BOTH token and user and the next run takes the
                // logged-out branch below.
                return;
            }
            // Logged out is a KNOWN-empty state, not an unknown one.
            setPermissions([]); setRole(''); setOrgRole('member'); setPillarRoles({});
            setEffective([]); setScoped([]); setContactScope('all');
            setError(false); setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(`${API}/auth/permissions`, { headers: getAuthHeader() });
            // Legacy keys (role/permissions/org_role/pillar_roles) stay the
            // back-compat surface; effective[]/scoped[] are additive (#663/#662).
            setPermissions(res.data.permissions || []);
            setRole(res.data.role || '');
            setOrgRole(res.data.org_role || 'member');
            setPillarRoles(res.data.pillar_roles || {});
            setEffective(Array.isArray(res.data.effective) ? res.data.effective : []);
            setScoped(Array.isArray(res.data.scoped) ? res.data.scoped : []);
            setContactScope(res.data.contact_scope === 'assigned' ? 'assigned' : 'all');
            setError(false);
        } catch (err) {
            // Fail closed WITHOUT wiping: keep the last-known permission state
            // so a transient failure doesn't blank already-rendered UI, and let
            // `permissionsUnknown` carry the "this answer failed" signal.
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [user, getAuthHeader]);

    useEffect(() => { loadPermissions(); }, [loadPermissions]);

    // Manual re-run of the fetch (the PlanContext.retry analogue).
    const refetch = useCallback(() => loadPermissions(), [loadPermissions]);

    // "We have not been told yet, or the last answer failed." Distinct from
    // can() === false, which means "we were told no" (PlanContext.planUnknown
    // pattern). A 200 with empty permissions is KNOWN-empty: unknown is false.
    // A parked fetch (token present, /auth/me still hydrating the user) also
    // reads as unknown: initial loading=true simply persists until the fetch
    // actually runs (F-002 cold-load fix).
    const permissionsUnknown = loading || error;

    // Flat `can` unions the legacy `permissions` key with the pillar-aware
    // `effective[]` key: on a pre-#663 backend effective is [] and the union
    // is exactly today's behavior; on the new backend it closes the
    // legacy-only vs pillar-aware resolution gap at the client.
    const allPermissions = useMemo(() => {
        const seen = new Set();
        for (const p of permissions) if (p) seen.add(p);
        for (const p of effective) if (p) seen.add(p);
        return Array.from(seen);
    }, [permissions, effective]);

    // Backward-compat flat check. With a scope argument it becomes the #662
    // scoped check: true iff some scoped[] entry for exactly that scope lists
    // the permission. Flat lookups never consult scoped[].
    const can = useCallback((permission, scope) => {
        if (scope) {
            return (scoped || []).some(entry =>
                entry?.scope_type === scope.type &&
                entry?.scope_id === scope.id &&
                (entry.permissions || []).includes(permission)
            );
        }
        return allPermissions.includes(permission);
    }, [allPermissions, scoped]);

    // The pillar- and assignment-aware answer ONLY (#860 C6). `can()` unions the
    // legacy `permissions` key, which would show an owner control to a manager
    // without a sales pillar, a read-only sales manager or a user with DENY rows —
    // all of whom the backend refuses. Owner controls gate on this instead; the
    // server's structured 403 stays the authority. Fails closed while unknown.
    const hasEffectivePermission = useCallback((permission) => {
        if (permissionsUnknown) return false;
        return (effective || []).includes(permission);
    }, [effective, permissionsUnknown]);

    // Is user an org-level admin? (Issue #558 — shared predicate: owner | org_admin | is_admin)
    const isOrgAdmin = useCallback(() => {
        return isOrgAdminUser({ ...user, org_role: orgRole });
    }, [orgRole, user]);

    // Does user have any role in a specific pillar?
    const hasPillarAccess = useCallback((pillarId) => {
        if (permissionsUnknown) return false; // fail closed until the answer is known
        if (!pillarId) return true;
        if (isOrgAdminUser({ ...user, org_role: orgRole })) return true;
        // No pillar_roles set = legacy user, show all pillars
        if (!pillarRoles || Object.keys(pillarRoles).length === 0) return true;
        return !!pillarRoles[pillarId];
    }, [pillarRoles, orgRole, user, permissionsUnknown]);

    // Is user read-only in a specific pillar?
    const isReadOnly = useCallback((pillarId) => {
        if (permissionsUnknown) return true; // fail closed until the answer is known
        if (!pillarId) return false;
        if (isOrgAdminUser({ ...user, org_role: orgRole })) return false;
        if (!pillarRoles || Object.keys(pillarRoles).length === 0) return false;
        const pr = pillarRoles[pillarId];
        if (!pr) return true; // no access = read-only equivalent
        return pr.read_only || false;
    }, [pillarRoles, orgRole, user, permissionsUnknown]);

    // Check a specific permission within a pillar
    const canInPillar = useCallback((pillarId, permission) => {
        if (isOrgAdminUser({ ...user, org_role: orgRole })) return true;
        if (!pillarRoles || Object.keys(pillarRoles).length === 0) {
            // Legacy user, use flat permissions (same union as `can`)
            return allPermissions.includes(permission);
        }
        const pr = pillarRoles[pillarId];
        if (!pr) return false;
        return (pr.permissions || []).includes(permission);
    }, [pillarRoles, orgRole, user, allPermissions]);

    return (
        <PermissionsContext.Provider value={{
            permissions, role, can,
            orgRole, pillarRoles,
            effective, scoped, contactScope: permissionsUnknown ? undefined : contactScope,
            permissionsLoading: loading,
            permissionsUnknown,
            permissionsError: error,
            refetch,
            hasPillarAccess, isReadOnly, canInPillar, isOrgAdmin, hasEffectivePermission,
        }}>
            {children}
        </PermissionsContext.Provider>
    );
}

export function usePermissions() {
    return useContext(PermissionsContext);
}
